<script lang="ts" setup>
// @ts-expect-error JS library
import { useMq } from 'vue3-mq'
import { storeToRefs } from 'pinia'
import type { Skill, Topic } from '@prisma/client'
import { useRoot } from '~/store'
import { useRadar } from '~/store/radar'
import { useLocaleThemePath } from '~/composables/locale'
import { capitalize } from '~/utils'

const page = '_doc'
const $radar = useRadar()

const { routeRaw } = storeToRefs($radar)
const { setWheelsCount } = useRoot()
const { xs, sm } = useMq()
const { t } = useI18n()
const i18nHead = useLocaleHead()

const location: string[] = (routeRaw.value?.params?.doc as string[]) || []

const { data: doc, error } = await useAsyncData(async () => await $fetch(`/api/doc/${location?.join('%2F')}`))

// Redirect to handle 1st level documents already crowled from Google
if (Array.isArray(location) && location.length === 1 && doc.value?.location?.includes('/'))
  navigateTo(`/assets/docs/${doc.value?.location}`, { redirectCode: 301 })

if (!doc.value || error.value) {
  const event = useRequestEvent()
  if (event) setResponseStatus(event, 404, 'Doc not found.')
}

$radar.setPreviousRoutePath('/assets')

useSeoMeta({
  title: () => t(`routes.${page}.title`, { name: capitalize(doc.value?.name) }),
  ogTitle: () => t(`routes.${page}.title`, { name: capitalize(doc.value?.name) }),
  description: () => t(`routes.${page}.description`, { name: capitalize(doc.value?.name) }),
  ogDescription: () => t(`routes.${page}.description`, { name: capitalize(doc.value?.name) })
})

useHead(i18nHead.value)

useStructuredData(page)

const format: 'md' | 'ipynb' | 'any' = doc.value?.name?.includes('.md')
  ? 'md'
  : doc.value?.name?.includes('.ipynb')
    ? 'ipynb'
    : 'any'

const filename: ComputedRef<string> = computed(() => (sm || xs ? `${doc.value?.name}` : `/${doc.value?.location}`))

// @ts-expect-error TODO: fix prisma types
const skills: ComputedRef<Skill[]> = computed(() => doc.value?.skills?.map?.(({ skill }) => skill) || [])
// @ts-expect-error TODO: fix prisma types
const topics: ComputedRef<Topic[]> = computed(() => doc.value?.topics?.map?.(({ topic }) => topic) || [])

setWheelsCount(skills.value.length > 0 ? 2 : 1)
</script>

<template lang="pug">
section.document__details.container-fluid
  .row(v-if="doc")
    .col-12
      .document__details__hero
        UIMarkdownReader.w-full(
          v-if="format === 'md'"
          :markdown="doc.fileContent"
          :filename="filename"
        )
        UIJupyterReader(
          v-else-if="format === 'ipynb'"
          :ipynb="doc.fileContent"
          :filename="filename"
        )
  .row(v-else)
    .col-12
      .document__404
        h1 Error 404 - Documentation Not Found.
        p The server failed to retrieve the documentation:
          span {{ location }}.
        UIGridImage(
          src="https://s3.rottigni.tech/portfolio.rottigni.tech/static_content/404/hero-404-2.jpg"
          :height="4"
        )
        NuxtLink(:to="useLocaleThemePath(`/`)") Home
  .row(v-if="skills && Array.isArray(skills) && skills.length > 0")
    .col-12
      .documents__details__report
        .documents__details__report__skills
          CardSkill(
            v-for="(skill, index) in skills"
            :key="`skill-${index}`"
            :skill="skill"
          )
        .documents__details__report__topics
          span(
            v-for="(topic, index) in topics.filter(topic => !!topic.name)"
            :key="`topic-${index}`"
            :style="`background-color: ${topic?.color}`"
          ) {{ topic.name }}
</template>

<style lang="sass" scoped>
.row
  @apply w-full h-[100dvh] overflow-hidden relative
.document__details
    @apply w-full h-full flex-center flex-col
    .document__details__hero,
    .documents__details__report
      @apply w-full h-full flex-center flex-col gap-8
      .documents__details__report__topics,
      .documents__details__report__skills
        @apply w-full max-w-[800px] flex-center flex-wrap gap-2
        span
          @apply rounded-md text-xs lg:text-lg px-2 lg:px-3 py-1 lg:py-2 uppercase whitespace-nowrap
.document__404
  @apply flex h-full flex-col justify-center relative
  h1
    @apply text-primary text-center text-2xl lg:text-4xl pb-6
  p
    @apply w-full text-lg lg:text-2xl text-white text-center max-w-[600px] mx-auto
    span
      @apply text-secondary mx-1
  a
    @apply block w-full text-center text-primary underline text-2xl
</style>
